import * as React from "react";
import { Search, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";

import type { SearchFilterBarProps } from "./types";

import { FilterButton, SortButton } from "./components";

export function SearchFilterBar({
  placeholder = "Search by role, action...",
  searchValue = "",
  onSearchChange,
  onSearch,
  // Legacy props
  filterFields = [],
  filters = [],
  onFiltersChange,
  sortFields = [],
  sorts = [],
  onSortsChange,
  // New filter-builder compatible props
  filterableAttributes = [],
  dynamicFilters = [],
  onAddFilter,
  onUpdateFilter,
  onRemoveFilter,
  onClearFilters,
  onForeignKeySearch,
  // New sort-builder compatible props
  sortableAttributes = [],
  dynamicSorts = [],
  onAddSort,
  onUpdateSort,
  onRemoveSort,
  onClearSorts,
  // Create button
  showCreateButton = false,
  onCreateClick,
  createButtonLabel = "Create",

  // for add additional filter in one container
  addOnFilter = [],
}: SearchFilterBarProps) {
  const useDynamicFilters = filterableAttributes.length > 0 && onAddFilter;
  const useDynamicSorts = sortableAttributes.length > 0 && onAddSort;

  // Legacy handlers are kept for API compatibility but not used here,
  // since FilterButton/SortButton encapsulate the UI and logic.

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      onSearch?.();
    }
  };

  // Check if filter/sort should be shown
  const showFilterButton = useDynamicFilters
    ? filterableAttributes.length > 0
    : filterFields.length > 0;
  const showSortButton = useDynamicSorts
    ? sortableAttributes.length > 0
    : sortFields.length > 0;

  return (
    <Card className="aero:mb-3 aero:!rounded-lg aero:!shadow-none aero:border-neutral-300 aero:!py-0">
      <CardContent className="aero:!p-4">
        <div className="aero:flex aero:flex-col aero:gap-3">
          {/* Search Row */}
          <div className="aero:flex aero:items-center aero:gap-3">
            {/* Search Input */}
            <div className="aero:flex aero:flex-col aero:gap-2 aero:flex-1">
              <label className="aero:text-sm aero:font-bold aero:text-foreground">
                Search
              </label>
              <div className="aero:relative">
                <Search className="aero:absolute aero:left-3 aero:top-1/2 aero:-translate-y-1/2 aero:h-5 aero:w-5 aero:text-neutral-400" />
                <Input
                  value={searchValue}
                  onChange={(e) => onSearchChange?.(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholder}
                  className="aero:h-10 aero:pl-10 aero:rounded aero:border-neutral-300"
                />
              </div>
            </div>
            {/* Additional Filter Components */}
            {addOnFilter && addOnFilter.length > 0 && (
              <>
                {addOnFilter.map((filterComponent, index) => (
                  <div className="aero:mt-7" key={index}>
                    {filterComponent}
                  </div>
                ))}
              </>
            )}

            {showFilterButton && (
              <FilterButton
                filterFields={filterFields}
                filters={filters}
                onFiltersChange={onFiltersChange}
                filterableAttributes={filterableAttributes}
                dynamicFilters={dynamicFilters}
                onAddFilter={onAddFilter}
                onUpdateFilter={onUpdateFilter}
                onRemoveFilter={onRemoveFilter}
                onClearFilters={onClearFilters}
                onForeignKeySearch={onForeignKeySearch}
              />
            )}

            {showSortButton && (
              <SortButton
                sortFields={sortFields}
                sorts={sorts}
                onSortsChange={onSortsChange}
                sortableAttributes={sortableAttributes}
                dynamicSorts={dynamicSorts}
                onAddSort={onAddSort}
                onUpdateSort={onUpdateSort}
                onRemoveSort={onRemoveSort}
                onClearSorts={onClearSorts}
              />
            )}

            {showCreateButton && (
              <Button
                type="button"
                intent="primary"
                design="solid"
                size="md"
                className="aero:mt-7"
                onClick={onCreateClick}
              >
                <Plus className="aero:h-5 aero:w-5" />
                <span className="aero:font-bold aero:text-sm">
                  {createButtonLabel}
                </span>
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
